import React, { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useLocation, useParams } from "react-router";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { LoadingContainer } from "go-core/components/Loading";
import { useMomentLocaleForLocation } from "go-core/hooks/useMomentLocale";
import { isWebView } from "go-core/utils/utils";
import { listFiltersEncode } from "go-list/core/components/Filter/services/decoder";
import { SegmentContext, SegmentListService } from "go-segment/context";
import LicenseExpiringNotification from "../../../../components/LicenseExpiringNotification/LicenseExpiringNotification";
import { organizationApiSegment } from "../../../../services/Api/Organization/organizationApiSegment";
import { InvitationApi } from "../../../../services/Api/Organization/organizationTypes";
import { api } from "../../../../services/Security/api";
import { useAuth } from "../../../../services/Security/auth";
import { isLicenseExpired } from "../../../../services/license";
import { selectOrganization } from "../../../../services/organizations/selectors";
import { selectUser } from "../../../../services/users/selectors";
import SecurityOrganizationAcceptTerms from "../../../security/OrganizationAcceptTerms/OrganizationAcceptTerms";
import OrganizationAcceptInvitation from "./components/OrganizationAcceptInvitation";
import OrganizationNotAcceptedTermsNotification from "./components/OrganizationNotAcceptedTermsNotification";
import OrganizationChatPage from "./pages/Chat";
import OrganizationDashboardPage from "./pages/Dashboard";
import OrganizationLoanPage from "./pages/Loan";
import OrganizationServiceAccessPage from "./pages/ServiceAccess";
import OrganizationSettingsPage from "./pages/Settings";
import OrganizationSubscriptionsPage from "./pages/Subscriptions";
import OrganizationUsersPage from "./pages/Users";

interface MatchParams {
	organizationId: string;
}

const OrganizationPage = () => {
	const organization = useSelector(selectOrganization);
	const user = useSelector(selectUser);
	const { t } = useTranslation();
	const { organizationId } = useParams<MatchParams>();
	const location = useLocation();
	const auth = useAuth();
	const { addFlash } = useFlash();
	const [loading, setLoading] = useState(true);
	const [invitation, setInvitation] = useState<InvitationApi>();
	const notAcceptedTerms = !organization.accept_terms || !organization.accept_privacy || !organization.accept_rules;
	useMomentLocaleForLocation(organization);
	const segmentPath = `/${organization.id}/settings/segments`;

	useEffect(() => {
		if (organizationId) {
			loginOrganization();
		}
	}, [organizationId]);

	const loginOrganization = async () => {
		setLoading(true);
		if (invitation !== undefined) setInvitation(undefined);
		try {
			await auth.loginOrganization(organizationId);
		} catch (err) {
			const filters = listFiltersEncode([
				{
					filterId: "status",
					condition: "e",
					value: "PENDING",
				},
				{
					filterId: "organization",
					condition: "e",
					value: organizationId,
				},
			]);
			const params = {
				include: "organization",
				f: filters,
			};
			const invitations = await api.user().getMeInvitations(params);
			if (invitations.length === 0) {
				handleError.alert(err, addFlash);
			} else {
				setInvitation(invitations[0]);
			}
		}
		setLoading(false);
	};

	const checkIfRenderCustomTopStyleWhenNotAcceptedTermsByOrganization = () => {
		if (location.pathname !== `/${organization.id}/accept_terms`) {
			if (organization.id && notAcceptedTerms) return true;
			return false;
		}
		return false;
	};

	const getTopStyleForLicenseExpiringNotification = (): string => {
		if (!user?.verified) {
			if (isLicenseExpired(organization.license_expires_at, organization.license_type, organization.timezone)) {
				return "46px";
			}
			return "0";
		}
		return "0";
	};

	const getTopStyleForOrganizationNotAcceptedTermsNotification = (): string => {
		if (!user?.verified) {
			if (isLicenseExpired(organization.license_expires_at, organization.license_type, organization.timezone)) {
				if (checkIfRenderCustomTopStyleWhenNotAcceptedTermsByOrganization()) return "92px";
				return "46px";
			}
			if (checkIfRenderCustomTopStyleWhenNotAcceptedTermsByOrganization()) return "46px";
			return "0";
		}
		if (isLicenseExpired(organization.license_expires_at, organization.license_type, organization.timezone)) {
			if (checkIfRenderCustomTopStyleWhenNotAcceptedTermsByOrganization()) return "46px";
			return "0";
		}
		if (checkIfRenderCustomTopStyleWhenNotAcceptedTermsByOrganization()) return "0";
		return "0";
	};

	if (loading) {
		return <LoadingContainer />;
	}

	if (invitation) {
		return <OrganizationAcceptInvitation invitation={invitation} onAccept={loginOrganization} />;
	}

	if (!organization.id) {
		return (
			<div className="content">
				<div className="content-preview mt-3">
					<Alert variant={"danger"}>{t("constraints.access_forbidden", { ns: "lib" })}</Alert>
				</div>
			</div>
		);
	}

	if (notAcceptedTerms && !organization.admin && !organization.partner) {
		return (
			<>
				<SecurityOrganizationAcceptTerms />
			</>
		);
	}

	if (!notAcceptedTerms && location.pathname === `/${organization.id}/accept_terms`) {
		return <Redirect to={`/${organization.id}`} />;
	}

	return (
		<SegmentContext.Provider
			value={
				new SegmentListService(
					{
						saveSegment: organizationApiSegment.saveSegment,
						removeSegment: organizationApiSegment.removeSegment,
						getSegments: organizationApiSegment.getSegments,
						restoreSegment: organizationApiSegment.restoreSegment,
						updateSegment: organizationApiSegment.updateSegment,
						updateSegments: organizationApiSegment.updateSegments,
						setAsDefault: organizationApiSegment.setSegmentAsDefault,
						setNotDefault: organizationApiSegment.setSegmentNotDefault,
					},
					segmentPath,
					organizationApiSegment.getListInfo,
					addFlash
				)
			}
		>
			{!isWebView && (
				<>
					<LicenseExpiringNotification
						organization={organization}
						topStyle={getTopStyleForLicenseExpiringNotification()}
					/>
					<OrganizationNotAcceptedTermsNotification
						organization={organization}
						topStyle={getTopStyleForOrganizationNotAcceptedTermsNotification()}
					/>
				</>
			)}
			<Switch>
				<Route path={"/:organizationId/subscriptions"} component={OrganizationSubscriptionsPage} />
				<Route path={"/:organizationId/users"} component={OrganizationUsersPage} />
				<Route path={"/:organizationId/settings"} component={OrganizationSettingsPage} />
				<Route path={"/:organizationId/loans"} component={OrganizationLoanPage} />
				<Route path={`/:organizationId/help`} component={OrganizationServiceAccessPage} />
				<Route path={`/:organizationId/chat`} component={OrganizationChatPage} />
				<Route exact path={"/:organizationId/accept_terms"} component={SecurityOrganizationAcceptTerms} />
				<Route exact path={"/:organizationId"} component={OrganizationDashboardPage} />
			</Switch>
		</SegmentContext.Provider>
	);
};

export default OrganizationPage;
